import { MapMarker } from "../map.types";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

interface ClusterMarkerOptions {
  count: number;
  position: google.maps.LatLng;
  color: string;
}

export interface RegionCluster {
  regionId: string;
  regionColor: string;
  markers: google.maps.Marker[];
  clusterer?: MarkerClusterer;
}

export const createRegionClusters = (
  markers: MapMarker[],
  markersData: { [key: string]: google.maps.Marker },
  map: google.maps.Map | null | undefined,
  maxClusterZoom = 12,
): MarkerClusterer[] => {
  const regionClusters = markers.reduce((clusters: RegionCluster[], marker) => {
    const regionId = marker.region?.id || "unassigned";
    const regionColor = marker.region?.color?.toLocaleLowerCase() || "#494547";
    let cluster = clusters.find((c) => c.regionId === regionId);

    if (!cluster) {
      cluster = { regionId, regionColor, markers: [] };
      clusters.push(cluster);
    }

    // Add marker reference based on key - only if it exists
    const markerInstance = markersData[marker.id];
    if (markerInstance) {
      cluster.markers.push(markerInstance);
    }

    return clusters;
  }, []);

  return regionClusters.map((regionCluster) => {
    const clusterer = new MarkerClusterer({
      map,
      algorithmOptions: {
        maxZoom: maxClusterZoom,
      },
      renderer: {
        render: ({ count, position }) => {
          const color = regionCluster.regionColor;
          return createClusterMarker({
            count,
            position,
            color,
          });
        },
      },
    });

    // Add markers for the current region to the clusterer
    clusterer.addMarkers(regionCluster.markers);
    regionCluster.clusterer = clusterer;

    return clusterer;
  });
};

export const createClusterMarker = ({
  count,
  position,
  color,
}: ClusterMarkerOptions) => {
  // Create SVG with the specified fill color and count
  const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="40" height="40">
    <circle cx="120" cy="120" opacity=".6" r="70" />
    <circle cx="120" cy="120" opacity=".3" r="90" />
    <circle cx="120" cy="120" opacity=".2" r="110" />
    <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
  </svg>`;

  // Create a standard google.maps.Marker with custom icon
  return new google.maps.Marker({
    position,
    title: `Cluster of ${count} markers`,
    icon: {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 20),
    },
    zIndex: 1000 + count,
  });
};
